import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, PawPrint, ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { CLINICS } from "../../data/clinics";

const NAV = [
  { label: "Sobre", to: "/sobre", testId: "nav-sobre" },
  { label: "Serviços", to: "/servicos", testId: "nav-servicos" },
  { label: "Contactos", to: "/contactos", testId: "nav-contactos" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [lang, setLang] = useState("PT");
  const [clinicsOpen, setClinicsOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#FAF7F2]/85 backdrop-blur-xl shadow-[0_1px_0_rgba(30,95,110,0.08),0_8px_24px_-12px_rgba(15,58,68,0.18)]"
          : "bg-[#FAF7F2]/60 backdrop-blur-md"
      }`}
    >
      <div className="container-app flex h-16 md:h-20 items-center justify-between gap-6">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center group"
          aria-label="Vet Clinic — Página inicial"
        >
          <span className="inline-flex items-center rounded-2xl bg-[#E9F4F4] px-3 py-1.5 border border-[#1E5F6E]/10 shadow-sm transition-transform group-hover:-translate-y-0.5">
            <img
              src="https://customer-assets.emergentagent.com/job_clinic-booking-dev/artifacts/nr4pdmyv_LOGO%20VETCLINIC.png"
              alt="Vet Clinic"
              className="h-9 md:h-10 w-auto object-contain"
            />
          </span>
        </Link>

        <nav
          data-testid="header-nav"
          className="hidden lg:flex items-center gap-1"
        >
          {NAV.slice(0, 2).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className="px-3.5 py-2 rounded-lg text-[15px] font-medium text-[#1A1A1A]/80 hover:text-[#1E5F6E] hover:bg-[#1E5F6E]/5 transition-colors"
            >
              {item.label}
            </Link>
          ))}
          <div
            className="relative"
            onMouseEnter={() => setClinicsOpen(true)}
            onMouseLeave={() => setClinicsOpen(false)}
          >
            <button
              data-testid="nav-clinicas"
              className="flex items-center gap-1 px-3.5 py-2 rounded-lg text-[15px] font-medium text-[#1A1A1A]/80 hover:text-[#1E5F6E] hover:bg-[#1E5F6E]/5 transition-colors"
              onClick={() => setClinicsOpen((v) => !v)}
              aria-expanded={clinicsOpen}
            >
              Clínicas
              <ChevronDown
                className={`h-4 w-4 transition-transform ${clinicsOpen ? "rotate-180" : ""}`}
              />
            </button>
            <AnimatePresence>
              {clinicsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-0 top-full pt-3 w-64"
                >
                  <div className="rounded-2xl border border-[#1E5F6E]/10 bg-white p-2 shadow-[0_20px_50px_-20px_rgba(15,58,68,0.25)]">
                    {CLINICS.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/clinicas/${c.slug}`}
                        data-testid={`nav-clinica-${c.slug}`}
                        className="block rounded-xl px-3 py-2.5 hover:bg-[#FAF7F2] transition-colors"
                      >
                        <div className="text-sm font-semibold text-[#1A1A1A]">
                          {c.nome}
                        </div>
                        <div className="text-xs text-[#5A5A5A] mt-0.5">
                          {c.cidade} • Madeira
                        </div>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {NAV.slice(2).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className="px-3.5 py-2 rounded-lg text-[15px] font-medium text-[#1A1A1A]/80 hover:text-[#1E5F6E] hover:bg-[#1E5F6E]/5 transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <div
            data-testid="lang-toggle"
            className="hidden md:flex items-center rounded-full bg-white/70 backdrop-blur border border-[#1E5F6E]/10 p-0.5 text-xs font-semibold"
          >
            {["PT", "EN"].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                data-testid={`lang-${l.toLowerCase()}`}
                className={`px-2.5 py-1 rounded-full transition-colors ${
                  lang === l
                    ? "bg-[#1E5F6E] text-white"
                    : "text-[#5A5A5A] hover:text-[#1E5F6E]"
                }`}
                aria-pressed={lang === l}
              >
                {l}
              </button>
            ))}
          </div>

          <Link
            to="/marcar-consulta"
            data-testid="header-book-cta"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#E8A87C] px-5 py-2.5 text-[14px] font-bold text-[#1A1A1A] shadow-sm hover:bg-[#D99A6E] hover:-translate-y-0.5 hover:shadow-md transition-all"
          >
            Marcar Consulta
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            className="inline-flex lg:hidden h-10 w-10 items-center justify-center rounded-xl bg-white/70 border border-[#1E5F6E]/10 text-[#1E5F6E]"
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] bg-[#FAF7F2] lg:hidden"
            data-testid="mobile-menu-overlay"
          >
            <div className="container-app flex h-16 md:h-20 items-center justify-between">
              <span className="inline-flex items-center rounded-2xl bg-[#E9F4F4] px-3 py-1.5 border border-[#1E5F6E]/10" aria-label="Vet Clinic">
                <img
                  src="https://customer-assets.emergentagent.com/job_clinic-booking-dev/artifacts/nr4pdmyv_LOGO%20VETCLINIC.png"
                  alt="Vet Clinic"
                  className="h-9 w-auto object-contain"
                />
              </span>
              <button
                data-testid="mobile-menu-close"
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white border border-[#1E5F6E]/10 text-[#1E5F6E]"
                onClick={() => setMobileOpen(false)}
                aria-label="Fechar menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <motion.nav
              initial="hidden"
              animate="show"
              variants={{
                hidden: {},
                show: { transition: { staggerChildren: 0.04 } },
              }}
              className="container-app mt-6 flex flex-col gap-1"
            >
              {NAV.slice(0, 2).map((item) => (
                <motion.div
                  key={item.to}
                  variants={{
                    hidden: { opacity: 0, y: 8 },
                    show: { opacity: 1, y: 0 },
                  }}
                >
                  <Link
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className="block px-4 py-4 text-2xl font-display font-bold text-[#1A1A1A] border-b border-[#1E5F6E]/10"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              <div className="px-4 py-4 border-b border-[#1E5F6E]/10">
                <div className="text-2xl font-display font-bold text-[#1A1A1A] mb-3">
                  Clínicas
                </div>
                <div className="flex flex-col gap-2">
                  {CLINICS.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/clinicas/${c.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className="text-base font-medium text-[#5A5A5A]"
                    >
                      → {c.nome}
                    </Link>
                  ))}
                </div>
              </div>
              {NAV.slice(2).map((item) => (
                <motion.div
                  key={item.to}
                  variants={{
                    hidden: { opacity: 0, y: 8 },
                    show: { opacity: 1, y: 0 },
                  }}
                >
                  <Link
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className="block px-4 py-4 text-2xl font-display font-bold text-[#1A1A1A] border-b border-[#1E5F6E]/10"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              <div className="mt-8 px-4">
                <Link
                  to="/marcar-consulta"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center w-full rounded-full bg-[#E8A87C] px-6 py-4 text-base font-bold text-[#1A1A1A]"
                >
                  Marcar Consulta
                </Link>
                <div className="mt-6 flex items-center justify-center gap-2">
                  {["PT", "EN"].map((l) => (
                    <button
                      key={l}
                      onClick={() => setLang(l)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold ${
                        lang === l
                          ? "bg-[#1E5F6E] text-white"
                          : "bg-white text-[#5A5A5A] border border-[#1E5F6E]/10"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
